import { useGetAccountsQuery } from "@/modules/account/services";
import type {
  IGroupTransferBeneficiary,
  IGroupTransferForm,
} from "@/modules/transaction/common/types";
import { useGetGroupTransferQuery } from "@/modules/transaction/services";
import { groupTransferValidationSchema } from "@/modules/transaction/validations/validationSchema";
import { DATE_FORMAT, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import { CloudflareAsset, Currencies, SideSheetPosition, StaticUrl, } from "@/shared/common/enums";
import type { IOperationPageProps } from "@/shared/common/types";
import { CloudFlareImage, InfoLinks, PageHeader } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import { useMediaQuery, useTranslationExists } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { GroupTransferBeneficiaryList } from "./GroupTransferBeneficiaryList";
import { GroupTransferForm } from "./GroupTransferForm";

export const GroupTransfer = ({ isCopy, isEdit }: IOperationPageProps) => {
  const { t, exists } = useTranslationExists();
  const { operationId = "" } = useParams();
  const isDesktop = useMediaQuery();
  const [isSideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const [
    groupTransferBeneficiariesList,
    setGroupTransferBeneficiariesList,
  ] = useState<IGroupTransferBeneficiary[]>([]);
  const { data: accountsList = [] } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: groupTransfer,
    isLoading: isGetTransferLoading,
  } = useGetGroupTransferQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const initialValues: IGroupTransferForm = {
    transactionNumber: "",
    debitAccount: "",
    beneficiaries: [],
    scheduleDate: formatDate(new Date(), DATE_FORMAT.YYYY_MM_DD),
    amount: "",
    purpose: t("transfer.group.purpose"),
    currency: Currencies.AMD,
    commissionAccount: "",
  };

  useEffect(() => {
    if ((isEdit || isCopy) && groupTransfer?.beneficiaries) {
      setGroupTransferBeneficiariesList(groupTransfer.beneficiaries);
    }
  }, [isEdit, isCopy, groupTransfer]);

  return (
    <>
      <div className="full-width page-container form-layout">
        <PageHeader>
          {!isSideBarOpen && exists("transfer.sidebarTitle") && (
            <Button
              type="secondary"
              size="small"
              className="side-sheet-btn"
              data-testid="open-info-side-sheet"
              data-ga-id="open-info-side-sheet"
              data-ga-action="Open Group transfer info side sheet"
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Info}/>
                ),
                alignment: "left",
              }}
              onClick={() => setSideBarOpen(!isSideBarOpen)}
            />
          )}
        </PageHeader>
        <div className="flexbox overflow-hidden mb-64 page-body full-width justify-content--center">
          <div className="group-transfer">
            <div className="group-transfer-beneficiaries">
              <Heading
                size="xsmall"
                weight="bolder"
                className="mb-20 group-transfer-title-desk"
              >
                {t("transfer.group.title")}
              </Heading>
              <GroupTransferBeneficiaryList
                setGroupTransferBeneficiariesList={setGroupTransferBeneficiariesList}
                groupTransferBeneficiariesList={groupTransferBeneficiariesList}
                accountsList={accountsList}
              />
            </div>
            <div className="group-transfer-form-wrapper">
              <Heading
                size="xsmall"
                weight="bolder"
                className="mb-20 group-transfer-title-mob"
              >
                {t("transfer.group.title")}
              </Heading>
              <div id="form-container" className="group-transfer-form">
                <FormContainer
                  mode="onBlur"
                  reValidateMode="onBlur"
                  initialValues={initialValues}
                  validationScheme={groupTransferValidationSchema({ t, accountsList })}
                >
                  <GroupTransferForm
                    accountsList={accountsList}
                    isEdit={isEdit}
                    isCopy={isCopy}
                    groupTransfer={groupTransfer}
                    isGetTransferLoading={isGetTransferLoading}
                    groupTransferBeneficiariesList={groupTransferBeneficiariesList}
                  />
                </FormContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isSideBarOpen}
        onClose={() => setSideBarOpen(false)}
        title={t("transfer.sidebarTitle")}
      >
        <div className="flexbox flex-wrap--wrap">
          <Text type="primary" className="mb-10">
            {t("transfer.group.sidebarMessage")}
          </Text>
          <InfoLinks keys={[StaticUrl.CorporateTariffs, StaticUrl.CardTariffs]}/>
        </div>
      </SideSheet>
    </>
  );
};
